import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS } from '@/constants';
import { wizardEntryFor, type PartKey } from '@/pages/Sessions/interviewStatus';
import type { InterviewTemplate } from '@/services/api';
import type { AnswerMode } from './interviewStore';

/** Lifted out of Sessions.tsx (not local to SetupWizard) because
 * PastSessionsList's Resume/Try-again actions must mutate the same state
 * the wizard reads — see loadIntoCard in Sessions.tsx. Persisted so it
 * survives navigating away (e.g. to Packages) and back; cleared on sign-out
 * (see MainLayout.tsx's handleLogout/handleEndGuestSession) so it never
 * leaks into the next person's session on a shared browser. */
export interface WizardState {
  templateId: string | null;
  part: PartKey | null;
  mode: AnswerMode | null;
  step: number;
  /** Bumped on every loadIntoCard call so the wizard's scroll+pulse effect
   * re-fires even if the exact same session is loaded twice in a row. */
  flashNonce: number;
}

export const initialWizardState: WizardState = {
  templateId: null,
  part: null,
  mode: null,
  step: 0,
  flashNonce: 0,
};

interface SessionWizardStore {
  wizard: WizardState;
  setWizard: (updater: WizardState | ((prev: WizardState) => WizardState)) => void;
  resetWizard: () => void;
  /** Loads a template straight into the setup card (see wizardEntryFor for
   * which step it lands on) and flashes it — used when coming back from a
   * finished questionnaire, so the other one is right there instead of the
   * card starting over at "Select interview". */
  enterTemplate: (template: Pick<InterviewTemplate, 'id' | 'htsRequired'>, part: PartKey | null) => void;
}

export const useSessionWizardStore = create<SessionWizardStore>()(
  persist(
    (set) => ({
      wizard: initialWizardState,
      setWizard: (updater) =>
        set((state) => ({
          wizard: typeof updater === 'function' ? updater(state.wizard) : updater,
        })),
      resetWizard: () => set({ wizard: initialWizardState }),
      enterTemplate: (template, part) =>
        set((state) => ({
          wizard: { ...wizardEntryFor(template, part), flashNonce: state.wizard.flashNonce + 1 },
        })),
    }),
    {
      name: STORAGE_KEYS.SESSION_WIZARD,
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export default useSessionWizardStore;
